import { ChevronLeft, ChevronRight, LayoutGrid, CheckCircle2, XCircle, Lightbulb, LogOut, Flag } from "lucide-react";
import { QuestionStem } from "./QuestionStem";
import { OptionList } from "./OptionList";
import { FeedbackLink } from "./FeedbackLink";

export const StudyView = ({ es, q, pos, setAnswer, toggleMark, setHighlights, toggleStrike, next, prev, doCheck, openReview, exit }) => {
  const posKey = String(pos);
  const checked = es.checked?.[posKey];
  const answered = es.answers?.[posKey] != null;
  const isMarked = es.marked.includes(pos);
  const tally = Object.values(es.checked || {});
  const correctCount = tally.filter((c) => c.is_correct).length;

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="study-view">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 h-10 flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Study · Exam {es.exam_number}</span>
          <div className="flex items-center gap-2">
            <span data-testid="study-tally" className="flex items-center gap-2 text-xs font-mono bg-slate-100 px-2 py-0.5 rounded-md">
              <span className="flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" />{correctCount}</span>
              <span className="flex items-center gap-1 text-rose-600"><XCircle className="w-3.5 h-3.5" />{tally.length - correctCount}</span>
              <span className="text-slate-500">{tally.length}/{es.total_questions}</span>
            </span>
            <button data-testid="review-screen-btn" onClick={openReview}
              title="Review Screen — see every question's status at a glance and jump anywhere"
              className="p-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors">
              <LayoutGrid className="w-3.5 h-3.5" />
            </button>
            <button data-testid="mark-review-btn" onClick={toggleMark}
              title="Mark this question to revisit it later"
              className={`p-1.5 rounded-md border transition-colors ${isMarked ? "border-amber-400 bg-amber-50 text-amber-700" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
              <Flag className={`w-3.5 h-3.5 ${isMarked ? "fill-amber-400 text-amber-500" : ""}`} />
            </button>
            <button data-testid="exit-study-btn" onClick={exit} title="Save and exit to dashboard"
              className="p-1.5 text-slate-400 hover:text-slate-700 rounded-md hover:bg-slate-100 transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between mb-1.5">
          <span data-testid="question-counter" className="text-sm font-medium text-slate-900">
            Question {pos} of {es.total_questions}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">{q?.domain}</span>
        </div>

        {!q ? (
          <div className="animate-pulse space-y-4 mt-4">
            <div className="h-4 bg-slate-200 rounded w-3/4" />
            <div className="h-4 bg-slate-200 rounded w-full" />
            <div className="h-24 bg-slate-200 rounded" />
          </div>
        ) : (
          <>
            <QuestionStem
              stem={q.stem}
              imageUrl={q.image_url}
              highlights={es.highlights?.[posKey] || []}
              onChange={setHighlights}
            />
            <OptionList
              options={q.options}
              selected={es.answers?.[posKey]}
              struck={es.strikeouts?.[posKey] || []}
              correctIndex={checked ? q.correct_index : null}
              onSelect={setAnswer}
              onStrike={toggleStrike}
              disabled={!!checked}
            />

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              {!checked ? (
                <button
                  data-testid="check-answer-btn"
                  disabled={!answered}
                  onClick={doCheck}
                  className="bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white font-medium px-6 py-2 rounded-lg transition-colors"
                >
                  Check Answer
                </button>
              ) : <span />}
              <div className="flex items-center gap-2 ml-auto">
                <button data-testid="prev-question-btn" onClick={prev} disabled={pos <= 1}
                  title="Previous question"
                  className="flex items-center gap-1 text-sm font-medium px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40 transition-colors">
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>
                <button data-testid="next-question-btn" onClick={next} disabled={pos >= es.total_questions}
                  title="Next question (Enter also advances once an answer is selected)"
                  className="flex items-center gap-1 text-sm font-medium px-5 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-40 transition-colors">
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {checked && (
              <div
                data-testid="explanation-panel"
                className={`mt-4 rounded-xl border p-4 ${checked.is_correct ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}
                aria-live="polite"
              >
                <div className={`flex items-center gap-2 font-semibold text-sm mb-2 ${checked.is_correct ? "text-emerald-700" : "text-rose-700"}`}>
                  {checked.is_correct ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                  {checked.is_correct ? "Correct" : `Incorrect — correct answer is ${"ABCD"[q.correct_index]}`}
                </div>
                <div className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed">
                  <Lightbulb className="w-4 h-4 mt-0.5 shrink-0 text-slate-500" />
                  <p>{q.explanation}</p>
                </div>
                <div className="mt-3">
                  <FeedbackLink sessionId={es.id} questionNumber={pos} />
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};
